<template>
  <div class="leak-investigation-reports">
    <div class="row">
      <div class="col-lg-12">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h1 class="lbv-page-title pb-0">
            Claims Mitigation Reports
          </h1>
        </div>
          
        <LeakInvestigationReportsSearchFilters
          :filters="filters"
          :loading="loading"
          :exporting-csv="exportingCsv"
          :total-reports="totalReports"
          @update-filters="handleFiltersUpdate"
          @search="loadLeakInvestigationReports"
          @export-csv="handleExportCsv"
        />

        <div
          v-if="loading"
          class="text-center my-4"
        >
          <div
            class="spinner-border text-primary"
            role="status"
          >
            <span class="sr-only">Loading Claims Mitigation Reports...</span>
          </div>
        </div>

        <b-alert
          v-if="error"
          :show="!!error"
          variant="danger"
          dismissible
          @dismissed="error = null"
        >
          <strong>Error:</strong> {{ error }}
        </b-alert>

        <LeakInvestigationReportsTable
          v-if="!loading"
          :leak-investigation-reports="leakInvestigationReports"
          :download-states="downloadStates"
          :sort-by="sortField"
          :sort-desc="sortDirection === 'desc'"
          @download="handleDownload"
          @sort="handleSort"
        />

        <div
          v-if="!loading && leakInvestigationReports.length > 0"
          class="mt-3"
        >
          <b-pagination
            v-model="pageCurrent"
            :total-rows="totalReports"
            :per-page="pageSize"
            align="center"
            size="md"
            class="my-0"
            @change="handlePageChange"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch, computed, getCurrentInstance } from 'vue'
import moment from 'moment'
import LeakInvestigationReportsTable from '../components/leak-investigation-reports/LeakInvestigationReportsTable.vue'
import LeakInvestigationReportsSearchFilters from '../components/leak-investigation-reports/LeakInvestigationReportsSearchFilters.vue'
import reportServiceApi from '../services/reportServiceApi.js'

// Vue 2.7 compatible store access
const instance = getCurrentInstance()
const store = instance.proxy.$store
const lastInvoiceMonth = computed(() => store.state.lastInvoiceMonth)

const loading = ref(false)
const exportingCsv = ref(false)
const downloadStates = ref({})
const error = ref(null)
const leakInvestigationReports = ref([])
const pageCurrent = ref(1)
const pageSize = ref(20)
const totalReports = ref(0)
const sortField = ref('pdfLastModifiedAt')
const sortDirection = ref('desc')

const filters = reactive({
  partnerReference: '',
  workOrderNumber: '',
  leakbotId: '',
  dateFrom: null,
  dateTo: null,
  tenantId: null
})

const loadLeakInvestigationReports = async (page = pageCurrent.value) => {
  loading.value = true
  error.value = null
  
  try {
    const sortParams = {
      field: sortField.value,
      direction: sortDirection.value
    }
    
    const response = await reportServiceApi.getLeakInvestigationReports(filters, page, pageSize.value, sortParams)
    const parsed = reportServiceApi.parseJsonApiResponse(response)
    
    leakInvestigationReports.value = parsed.data
    totalReports.value = parsed.meta.page?.total || 0
    pageCurrent.value = page
  } catch (err) {
    console.error('Failed to load Claims Mitigation Reports:', err)
    error.value = err.detail || err.message || 'Failed to load Claims Mitigation Reports'
    leakInvestigationReports.value = []
    totalReports.value = 0
  } finally {
    loading.value = false
  }
}

const handleFiltersUpdate = (newFilters) => {
  Object.assign(filters, newFilters)
}

const handlePageChange = (page) => {
  pageCurrent.value = page
  loadLeakInvestigationReports(page)
}

const handleSort = ({ field, direction }) => {
  if (sortField.value === field) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortField.value = field
    sortDirection.value = direction
  }
  
  pageCurrent.value = 1  // Reset to first page when sorting
  loadLeakInvestigationReports(1)
}

const handleDownload = async (workOrderGuid) => {
  downloadStates.value = { ...downloadStates.value, [workOrderGuid]: 'in_progress' }
  error.value = null
  
  try {
    await reportServiceApi.downloadLeakInvestigationReport(workOrderGuid)
    downloadStates.value = { ...downloadStates.value, [workOrderGuid]: 'complete' }
  } catch (err) {
    console.error('Download failed:', err)
    error.value = err.detail || err.message || 'Failed to download report'
    // Remove state on error (revert to default)
    const newStates = { ...downloadStates.value }
    delete newStates[workOrderGuid]
    downloadStates.value = newStates
    console.log('Download states after error:', downloadStates.value)
  }
}

const handleExportCsv = async () => {
  exportingCsv.value = true
  error.value = null
  
  try {
    const sortParams = {
      field: sortField.value,
      direction: sortDirection.value
    }
    
    const result = await reportServiceApi.exportLeakInvestigationReports(filters, sortParams)
    
    if (result.success) {
      console.log(`CSV export successful: ${result.filename}`)
      // @TODO: Implement toast notification
    }
    
  } catch (err) {
    console.error('CSV export failed:', err)
    error.value = err.detail || err.message || 'Failed to export CSV'
  } finally {
    exportingCsv.value = false
  }
}

onMounted(() => {
  loadLeakInvestigationReports()
})

watch(
  () => [filters.partnerReference, filters.workOrderNumber, filters.leakbotId, filters.dateFrom, filters.dateTo, filters.tenantId],
  () => {
    pageCurrent.value = 1
  }
)

// Set default dateTo when lastInvoiceMonth becomes available from the store
watch(lastInvoiceMonth, (newVal) => {
  if (newVal && !filters.dateTo) {
    filters.dateTo = moment(newVal).endOf('month').format('YYYY-MM-DD')
  }
}, { immediate: true })
</script>
